import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import SEO from 'components/shared/SEO';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import ButtonText from 'components/shared/ButtonText';
import FormError from 'components/shared/FormError';
import Text from 'components/shared/Text';
import Heading from 'components/shared/Heading';
import ERRORS from 'constants/errors';
import STEPS from 'constants/steps';
import { actionSendEmailVerification, verifyEmail, stopLoading } from 'reduxConfig/auth.creators';
import { isMobileWebBrowser } from 'helpers/utils';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

function VerifyEmail({ dispatch, error, loading, email, message, timeout, step, isMobile }) {
  const { t } = useTranslation();
  useEffect(() => {
    dispatch(stopLoading());
  }, [dispatch]);

  // useEffect(() => {
  //   // this gets set as a `target` prop in a link in the VerifyEmailReturn route's component.
  //   window.name = STEPS.verifyEmailComplete;
  // }, []);

  if (step !== STEPS.verifyEmail) return <Redirect to={microapps.routes.login.path} />;

  const checkEmail = () => dispatch(verifyEmail());
  const resendEmail = () => dispatch(actionSendEmailVerification(email));

  return (
    <>
      <SEO title={t('auth_verify_meta_title')} />
      <Heading tag="h1">
        <Trans i18nKey="auth_verify_title" defaults="Verify your email" />
      </Heading>

      <Text tag="p" muted styles={[styles.belowHeaderText]}>
        <Trans
          i18nKey="auth_verify_subtitle"
          defaults="A verification email has been sent to {{email}}"
          values={{ email }}
        />
      </Text>

      {error && <FormError>{error !== ERRORS.emailTaken && t(error)}</FormError>}

      <ButtonPrimary fullWidth={isMobile} loading={loading} onClick={checkEmail} styles={[styles.button]}>
        <Trans i18nKey="auth_verify_check" defaults="Check verification" />
      </ButtonPrimary>

      <Text muted styles={[styles.resendText]}>
        <Trans i18nKey="auth_verify_not_received" defaults="Haven't received a code?" />{' '}
        {message && t(message)}
        {!message && timeout === 60 && (
          <ButtonText onClick={resendEmail} muted underlined>
            <Trans i18nKey="auth_verify_resend" defaults="Resend" />
          </ButtonText>
        )}
      </Text>
      {timeout < 60 && (
        <Text muted styles={[styles.resendText]}>
          <Trans
            i18nKey="auth_verify_timeout"
            defaults="You can request another code in {{timeout}} seconds"
            values={{ timeout }}
          />
        </Text>
      )}
      {isMobileWebBrowser && (
        <div className={styles.paddedRow}>
          <ButtonText href="/install">{t('auth_action_install')}</ButtonText>
        </div>
      )}
    </>
  );
}

VerifyEmail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  email: PropTypes.string,
  message: PropTypes.string,
  timeout: PropTypes.number,
  step: PropTypes.string,
  isMobile: PropTypes.bool.isRequired
};

VerifyEmail.defaultProps = {
  error: null,
  email: null,
  message: null,
  timeout: null,
  step: null
};

export default VerifyEmail;
